/**
 * API naming convention (aligned with orchestrator):
 * - McpServer = MCP server entity (what users see as "MCP server")
 * - McpTool = individual tool exposed by an MCP server
 */
import { useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { RequireAtLeastOne } from 'type-fest';

import { mcpEndpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import type { Context } from '@/shared/types/chat';
import type { Pagination } from '@/shared/types/common';
import type { ToolSettings } from '@/shared/types/toolSettings';

// Discovery & OAuth types
export type DiscoveryResult = {
  url: string;
  name?: string;
  auth: {
    mode: 'none' | 'header' | 'oauth';
    authorizationEndpoint?: string;
    tokenEndpoint?: string;
    registrationEndpoint?: string;
    scopesSuggested?: string[];
    headerSupported?: boolean;
    resourceUrl?: string;
  };
  capabilities: { tools: boolean };
};

export type OAuthStartRequest = {
  mcpUrl: string;
  authorizationEndpoint: string;
  tokenEndpoint: string;
  scopes?: string;
  clientId: string;
  clientSecret?: string;
  clientOrigin: string;
  resourceUrl?: string;
};

export type OAuthStartResult = { authUrl: string; state: string };

export type OAuthExchangeRequest = { code: string; state: string };

export type OAuthExchangeResult = {
  success: boolean;
  accessToken?: string;
  error?: { code: string; message: string };
};

export type RegisterRequest = {
  registrationEndpoint: string;
  redirectUri: string;
  clientName?: string;
};

export type RegisterResult = {
  success: boolean;
  clientId?: string;
  clientSecret?: string;
  error?: { code: string; message: string };
};

export type TestRequest = { url: string; headers?: Record<string, string> };

export type TestResult = {
  success: boolean;
  serverInfo?: { name?: string; version?: string };
  tools?: Array<{
    name: string;
    description?: string;
    inputSchema?: Record<string, unknown>;
    annotations?: McpToolAnnotations;
  }>;
  error?: { code: string; message: string };
};

// Tool invocation (MCP `tools/call`) types — used by the "Test tool" flow.
// The server resolves the endpoint and its credentials from the stored row itself, so the request
// only names which server to call: sending our own `url`/`headers` would let anyone who can write
// a brain drive an authenticated call to an endpoint of their choosing.
export type ToolCallRequest = {
  mcp_id: string;
  brain_id: string;
  name: string;
  arguments: Record<string, unknown>;
  /** Variables for the `moveo/context` envelope. The server fills in every other field. */
  context: Context & Record<string, unknown>;
};

export type McpContentBlock = { type: string; text?: string } & Record<string, unknown>;

export type ToolCallResult = {
  content: McpContentBlock[];
  structuredContent?: unknown;
  isError?: boolean;
  context?: Context & Record<string, unknown>;
};

export type ToolCallResponse = {
  success: boolean;
  duration_ms?: number;
  result?: ToolCallResult;
  error?: { code: string; message: string };
};

// MCP types
/** Hints the MCP server reports about a tool. The server is not verified, so never act on them. */
export type McpToolAnnotations = {
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
};

export type McpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: McpToolAnnotations;
};

export type McpConfig =
  | {
      auth_type: 'oauth';
      client_id: string;
      client_secret?: string;
      authorization_endpoint: string;
      token_endpoint: string;
      scopes?: string;
      access_token: string;
      refresh_token?: string;
      token_expires_at?: string;
      headers?: Record<string, string>;
    }
  | { headers?: Record<string, string> }; // No OAuth (optional custom headers)

export type McpAuthStatus = 'ok' | 'needs_reauth' | 'refreshing';
export type McpToolsStatus = 'ok' | 'needs_sync' | 'unreachable';

export type McpServer = {
  mcp_id: string;
  brain_id: string;
  digest?: string;
  last_used_at?: string;
  verified_at?: string;
  name: string;
  url: string;
  tools: McpTool[];
  /** A tool without a key uses `DEFAULT_TOOL_SETTINGS`. */
  tool_settings?: Record<string, Partial<ToolSettings>>;
  is_active: boolean;
  config: McpConfig;
  auth_status: McpAuthStatus;
  tools_status: McpToolsStatus;
  created_by?: string;
  created: string;
  updated: string;
};

export type McpServers = { mcp_servers: McpServer[] } & Pagination;

export type PartialMcpServer = RequireAtLeastOne<Partial<McpServer>, 'brain_id'>;

// API methods
export const API = Object.freeze({
  listMcpServers: async (brainId: string): Promise<McpServers> =>
    callGet(mcpEndpoints.mcps(brainId)),
  getMcpServer: async (brainId: string, mcpId: string): Promise<McpServer> =>
    callGet(mcpEndpoints.mcp(brainId, mcpId)),
  createMcpServer: async (brainId: string, server: Partial<McpServer>): Promise<McpServer> =>
    callPost(mcpEndpoints.mcps(brainId), server),
  updateMcpServer: async ({
    brain_id,
    mcp_id,
    ...server
  }: PartialMcpServer & { mcp_id: string }): Promise<McpServer> =>
    callPut(mcpEndpoints.mcp(brain_id, mcp_id), server),
  deleteMcpServer: async (brainId: string, mcpId: string): Promise<McpServer> =>
    callDelete(mcpEndpoints.mcp(brainId, mcpId)),
});

// Cache invalidation handlers
const defaultPagination: Pagination['pagination'] = {
  has_more: false,
  limit: 50,
  next_cursor: '',
  total: 0,
};

export const onMcpServerCreated = (queryClient: QueryClient, server: McpServer) => {
  queryClient.setQueryData<McpServers>([mcpEndpoints.mcps(server.brain_id)], (prev) => ({
    pagination: prev?.pagination ?? defaultPagination,
    mcp_servers: [...(prev?.mcp_servers ?? []), server],
  }));
};

export const onMcpServerUpdated = (queryClient: QueryClient, server: McpServer) => {
  queryClient.setQueryData<McpServers>([mcpEndpoints.mcps(server.brain_id)], (prev) => ({
    pagination: prev?.pagination ?? defaultPagination,
    mcp_servers: (prev?.mcp_servers ?? []).map((item) =>
      item.mcp_id === server.mcp_id ? { ...item, ...server } : item
    ),
  }));
  queryClient.setQueryData<McpServer>([mcpEndpoints.mcp(server.brain_id, server.mcp_id)], (prev) =>
    prev ? { ...prev, ...server } : server
  );
};

export const onMcpServerRemoved = (queryClient: QueryClient, brainId: string, mcpId: string) => {
  queryClient.setQueryData<McpServers>([mcpEndpoints.mcps(brainId)], (prev) => ({
    pagination: prev?.pagination ?? defaultPagination,
    mcp_servers: (prev?.mcp_servers ?? []).filter((item) => item.mcp_id !== mcpId),
  }));
  // Drop the single-server entry too: left behind it keeps serving the deleted server for the
  // default gcTime, so navigating back to its detail page would render a live-looking page that
  // re-connects to the third party and PUTs to a dead resource.
  queryClient.removeQueries({ queryKey: [mcpEndpoints.mcp(brainId, mcpId)] });
};

/**
 * `fetcher` builds its Error message from `errorData.error`, which the `/mcp` routes send as an
 * `{ code, message }` object rather than a string — stringifying that puts a literal
 * `[object Object]` in front of the user. `fetcher` also merges the parsed body onto the Error,
 * so the real message is still reachable as `error.error.message`.
 */
const readApiError = (error: unknown, fallback: string): string => {
  const nested = (error as { error?: { message?: unknown } })?.error;
  if (typeof nested?.message === 'string' && nested.message) {
    return nested.message;
  }
  const message = error instanceof Error ? error.message : '';
  return message && message !== '[object Object]' ? message : fallback;
};

/** Unwraps the `{"message": "..."}` some MCP servers nest inside their error string. */
const unwrapNestedMessage = (message: string): string =>
  message.match(/\{[^{}]*"message"\s*:\s*"([^"]+)"[^{}]*\}/)?.[1] ?? message;

const useMcpServers = (brainId?: string, mcpId?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Discovery mutation
  const discoverMutation = useMutation<DiscoveryResult, Error, string>({
    mutationFn: (url: string) => callPost(mcpEndpoints.discover, { url }),
  });

  // OAuth mutations
  const oauthStartMutation = useMutation<OAuthStartResult, Error, OAuthStartRequest>({
    mutationFn: (data) => callPost(mcpEndpoints.oauthStart, data),
  });

  const oauthExchangeMutation = useMutation<OAuthExchangeResult, Error, OAuthExchangeRequest>({
    mutationFn: (data) => callPost(mcpEndpoints.oauthExchange, data),
  });

  // Register mutation (RFC7591 Dynamic Client Registration)
  const registerMutation = useMutation<RegisterResult, Error, RegisterRequest>({
    mutationFn: (data) => callPost(mcpEndpoints.register, data),
  });

  // Test mutation
  const testMutation = useMutation<TestResult, Error, TestRequest>({
    mutationFn: async (data) => {
      // A rejected URL comes back as a 400, so `callPost` throws before `result` is ever read.
      let result: TestResult;
      try {
        result = await callPost(mcpEndpoints.test, data);
      } catch (error) {
        throw new Error(readApiError(error, t('mcp.test_error')));
      }
      if (!result.success) {
        throw new Error(unwrapNestedMessage(result.error?.message || t('mcp.test_error')));
      }
      return result;
    },
  });

  // Tool invocation mutation — failures are part of the payload (rendered as an error card),
  // so unlike testMutation this never throws on `success: false`. A rejected request is still a
  // 400, and that one does throw.
  const callToolMutation = useMutation<ToolCallResponse, Error, ToolCallRequest>({
    mutationFn: async (data) => {
      try {
        return await callPost(mcpEndpoints.call, data);
      } catch (error) {
        throw new Error(readApiError(error, t('mcp.test_error')));
      }
    },
  });

  // List MCP servers query
  const { data: mcpServersData, status: listStatus } = useQuery<McpServers, Error>({
    queryKey: [mcpEndpoints.mcps(brainId)],
    queryFn: () => API.listMcpServers(brainId),
    enabled: Boolean(brainId),
  });

  // Get MCP server query
  const { data: mcpServer, status: getStatus } = useQuery<McpServer, Error>({
    queryKey: [mcpEndpoints.mcp(brainId, mcpId)],
    queryFn: () => API.getMcpServer(brainId, mcpId),
    initialData: mcpServersData?.mcp_servers?.find((item) => mcpId === item.mcp_id),
    enabled: Boolean(brainId) && Boolean(mcpId),
  });

  // Create MCP server mutation
  const { mutate: createMcpServer, status: createStatus } = useMutation<
    McpServer,
    Error,
    Partial<McpServer>
  >({
    mutationFn: (data) => API.createMcpServer(brainId, data),
    onSuccess: (resp) => {
      onMcpServerCreated(queryClient, resp);
      toast.success(t('mcp.tool_created'));
    },
    meta: { errorToast: true },
  });

  // Update MCP server mutation
  const { mutate: updateMcpServer, status: updateStatus } = useMutation<
    McpServer,
    Error,
    PartialMcpServer & { mcp_id: string }
  >({
    mutationFn: API.updateMcpServer,
    onSuccess: (resp) => {
      onMcpServerUpdated(queryClient, resp);
      toast.success(t('mcp.tool_updated'));
    },
    meta: { errorToast: true },
  });

  // Delete MCP server mutation
  const { mutate: deleteMcpServer, status: deleteStatus } = useMutation<McpServer, Error, string>({
    mutationFn: (id) => API.deleteMcpServer(brainId, id),
    onSuccess: (resp) => {
      onMcpServerRemoved(queryClient, brainId, resp.mcp_id);
      toast.success(t('mcp.tool_deleted'));
    },
    meta: { errorToast: true },
  });

  const mcpServers = useMemo(
    () => mcpServersData?.mcp_servers ?? [],
    [mcpServersData?.mcp_servers]
  );

  return {
    // Discovery & OAuth
    discoverMutation,
    oauthStartMutation,
    oauthExchangeMutation,
    registerMutation,
    testMutation,
    callToolMutation,
    // CRUD operations
    mcpServers,
    mcpServer,
    listStatus,
    getStatus,
    createMcpServer,
    createStatus,
    updateMcpServer,
    updateStatus,
    deleteMcpServer,
    deleteStatus,
  };
};

export default useMcpServers;
